import type { MenuProps } from 'antd';
import { Layout, Menu } from 'antd';
import { useState } from 'react';
import { ReactComponent as Bills } from '../../../../assets/images/bill-icon.svg';
import { ReactComponent as Cars } from '../../../../assets/images/car-icon.svg';
import { ReactComponent as Dashboard } from '../../../../assets/images/dashboard-icon.svg';
import { ReactComponent as Demands } from '../../../../assets/images/demands-icon.svg';
import { ReactComponent as Financial } from '../../../../assets/images/financial-icon.svg';
import { ReactComponent as Help } from '../../../../assets/images/help-icon.svg';
import { ReactComponent as News } from '../../../../assets/images/news-icon.svg';
import { ReactComponent as Service } from '../../../../assets/images/service-icon.svg';
import { ReactComponent as Users } from '../../../../assets/images/users-icon.svg';
import useContent, { useVis } from '../../../../core/hooks/useContent';
import useRouterWithLang from '../../../../core/hooks/useRouterWithLang';
import { AppRouteKey } from '../../../../core/utilities/router';
import { useTranslate } from '../../../../i18n';
import './LeftMenu.scss';
import { useAppSelector } from '../../../../core/services/hooks';

const LeftMenu: React.FC = () => {
  const { lang, current, push } = useRouterWithLang();
  const { t } = useTranslate(lang, 'home');
  const content = useContent();
  const visibility = useVis();
  const me = useAppSelector((state) => state.app.setMe.me);
  const showCreditCard =
    process.env.REACT_APP_ENV !== 'production' ||
    me?.checkCreditCard ||
    me?.taxId === '25426087138';
  //console.log('Show:', showCreditCard);
  const items = [
    {
      label: content('hedefnet.menu.dashboard'),
      key: AppRouteKey.HOME,
      icon: <Dashboard />,
    },
    visibility('hedefnet.menu.user') && {
      label: content('hedefnet.menu.user'),
      key: AppRouteKey.USERS,
      icon: <Users />,
    },
    visibility('hedefnet.menu.demand') && {
      label: content('hedefnet.menu.demand'),
      key: t('demands'),
      icon: <Demands />,
      children: [
        visibility(
          'hedefnet.menu.demand.additional.product.service.request'
        ) && {
          label: content(
            'hedefnet.menu.demand.additional.product.service.request'
          ),
          key: AppRouteKey.DEMANDS,
        },
        visibility('hedefnet.menu.demand.process.request') && {
          label: content('hedefnet.menu.demand.process.request'),
          key: AppRouteKey.PROCESSUALDEMANDS,
        },
      ].filter(Boolean),
    },
    visibility('hedefnet.menu.fleet.vehicle.management') && {
      label: content('hedefnet.menu.fleet.vehicle.management'),
      key: 'cars',
      icon: <Cars />,
      children: [
        visibility(
          'hedefnet.menu.fleet.vehicle.management.fleet.vehicle.info'
        ) && {
          label: content(
            'hedefnet.menu.fleet.vehicle.management.fleet.vehicle.info'
          ),
          key: AppRouteKey.FLEETINFORMATION,
        },
        visibility(
          'hedefnet.menu.fleet.vehicle.management.vehicle.hgs.info'
        ) && {
          label: content(
            'hedefnet.menu.fleet.vehicle.management.vehicle.hgs.info'
          ),
          key: AppRouteKey.HGSINFO,
        },
        visibility(
          'hedefnet.menu.fleet.vehicle.management.vehicle.delivery'
        ) && {
          label: content(
            'hedefnet.menu.fleet.vehicle.management.vehicle.delivery'
          ),
          key: AppRouteKey.CARDELIVERY,
        },
        visibility('hedefnet.menu.fleet.vehicle.management.vehicle.return') && {
          label: content(
            'hedefnet.menu.fleet.vehicle.management.vehicle.return'
          ),
          key: AppRouteKey.CARRETURN,
        },
        visibility('hedefnet.menu.fleet.vehicle.management.document') && {
          label: content('hedefnet.menu.fleet.vehicle.management.document'),
          key: AppRouteKey.DOCUMENTS,
        },
      ].filter(Boolean),
    },
    visibility('hedefnet.menu.invoice') && {
      label: content('hedefnet.menu.invoice'),
      key: 'bills',
      icon: <Bills />,
      children: [
        visibility('hedefnet.menu.invoice.operational.bill') && {
          label: content('hedefnet.menu.invoice.operational.bill'),
          key: AppRouteKey.OPERATIONALBILL,
        },
        visibility('hedefnet.menu.invoice.financial.bill') && {
          label: content('hedefnet.menu.invoice.financial.bill'),
          key: AppRouteKey.FINANCIALBILL,
        },
        visibility('hedefnet.menu.invoice.additional.service.billa') && {
          label: content('hedefnet.menu.invoice.additional.service.billa'),
          key: AppRouteKey.ADDITIONALSERVICES,
        },
        visibility('hedefnet.menu.invoice.other.reflection.bill') && {
          label: content('hedefnet.menu.invoice.other.reflection.bill'),
          key: AppRouteKey.OTHERREFLECTION,
        },
        visibility('hedefnet.menu.invoice.problem.bill') && {
          label: content('hedefnet.menu.invoice.problem.bill'),
          key: AppRouteKey.PROBLEMATICBILL,
        },
      ].filter(Boolean),
    },
    visibility('hedefnet.menu.financial.operation') && {
      label: content('hedefnet.menu.financial.operation'),
      key: 'financial',
      icon: <Financial />,
      children: [
        visibility(
          'hedefnet.menu.financial.operation.balance.reconciliation.confirmation'
        ) && {
          label: content(
            'hedefnet.menu.financial.operation.balance.reconciliation.confirmation'
          ),
          key: AppRouteKey.BALANCECONFIRMATION,
        },
        visibility(
          'hedefnet.menu.financial.operation.current.account.statementa'
        ) && {
          label: content(
            'hedefnet.menu.financial.operation.current.account.statementa'
          ),
          key: AppRouteKey.CURRENTACCOUNTEXTRACT,
        },
        visibility(
          'hedefnet.menu.financial.operation.estimated.km.excess.calculation'
        ) && {
          label: content(
            'hedefnet.menu.financial.operation.estimated.km.excess.calculation'
          ),
          key: AppRouteKey.ESTIMATEDOVERKMCALCULATION,
        },
        visibility(
          'hedefnet.menu.financial.operation.early.return.penalty.fee.calculation'
        ) && {
          label: content(
            'hedefnet.menu.financial.operation.early.return.penalty.fee.calculation'
          ),
          key: AppRouteKey.EARLYRETURNPENALTYFEECALCULATION,
        },
        visibility('hedefnet.menu.financial.operation.invoice.payment') &&
          showCreditCard && {
            label: content('hedefnet.menu.financial.operation.invoice.payment'),
            key: AppRouteKey.CREDITCARDPAY,
          },
      ].filter(Boolean),
    },
    visibility('hedefnet.menu.service.operation') && {
      label: content('hedefnet.menu.service.operation'),
      key: 'service',
      icon: <Service />,
      children: [
        visibility('hedefnet.menu.service.operation.maintenance') && {
          label: content('hedefnet.menu.service.operation.maintenance'),
          key: AppRouteKey.MAINTENANCE,
        },
        visibility('hedefnet.menu.service.operation.breakdown') && {
          label: content('hedefnet.menu.service.operation.breakdown'),
          key: AppRouteKey.BREAKDOWN,
        },
        visibility('hedefnet.menu.service.operation.tire') && {
          label: content('hedefnet.menu.service.operation.tire'),
          key: AppRouteKey.TYRE,
        },
        visibility('hedefnet.menu.service.operation.damage') && {
          label: content('hedefnet.menu.service.operation.damage'),
          key: AppRouteKey.ACCIDENTALDAMAGE,
        },
      ].filter(Boolean),
    },
    visibility('hedefnet.menu.campaign.news.sponsorship') && {
      label: content('hedefnet.menu.campaign.news.sponsorship'),
      key: AppRouteKey.CAMPAIGNNEWS,
      icon: <News />,
      className: 'news',
    },
    visibility('hedefnet.menu.help') && {
      label: content('hedefnet.menu.help'),
      key: 'help',
      icon: <Help />,
      children: [
        visibility('hedefnet.menu.help.faq') && {
          label: content('hedefnet.menu.help.faq'),
          key: AppRouteKey.FAQ,
        },
        visibility('hedefnet.menu.help.guide') && {
          label: content('hedefnet.menu.help.guide'),
          key: AppRouteKey.GUIDE,
        },
        visibility('hedefnet.menu.help.feedback') && {
          label: content('hedefnet.menu.help.feedback'),
          key: AppRouteKey.FEEDBACK,
        },
        {
          label: content('hedefnet.menu.help.digital.assistant'),
          key: AppRouteKey.HELP_DIGITAL_ASSISTANT,
        },
        visibility('hedefnet.menu.help.driver.videos') && {
          label: content('hedefnet.menu.help.driver.videos'),
          key: AppRouteKey.DRIVERVIDEOS,
        },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  const getCurrentKey = (): string => {
    let currentItem = items.find((item) =>
      item.children?.find((child: any) => current.includes(child.key))
    );
    if (currentItem) {
      const children = currentItem.children?.find((child: any) =>
        current.includes(child.key)
      );
      return children?.key || '';
    }
    currentItem = items.find((item) => current.includes(item.key));
    if (currentItem) {
      return currentItem.key;
    }
    return current;
  };

  const onClick: MenuProps['onClick'] = (e) => {
    push(e.key);
  };

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (
      latestOpenKey &&
      items.findIndex((item) => item?.key === latestOpenKey) === -1
    ) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  return (
    <Layout.Sider className="max-xl:hidden" id="left-menu" width={282}>
      <Menu
        className="h-full py-6"
        onClick={onClick}
        selectedKeys={[getCurrentKey()]}
        mode="inline"
        items={items}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
      />
    </Layout.Sider>
  );
};

export default LeftMenu;
